// Shared non-component exports; keep component modules valid Fast Refresh boundaries.

import { type CustomDateRange, type RangeMode, type TimeRange } from "../lib/api";
import { resolvePeriodBoundsMs } from "../components/TimeRangeControls.helpers";

// ---------------------------------------------------------------------------
// Ticket 13 / Ticket 19 — Agent Profile "Assessment performance" section.
// Ticket 19 dropped the LLM narrative block and replaced it with the shared
// MeasureBarChart + CallCountCaption combo, and widened the section role
// gate to admit Admin + Manager (server-side scoping from Ticket 17). The
// gate runs in the parent; these components assume it already passed.
// ---------------------------------------------------------------------------

// Translate the page-level (TimeRange, RangeMode) pair into a concrete
// ISO from/to pair for the aggregate + per-result endpoints. Task #425
// consolidation: this is now a thin ISO-string wrapper over the shared
// ``resolvePeriodBoundsMs`` (components/TimeRangeControls.tsx), which is
// the single client-side equivalent of the server's
// ``compute_period_bounds`` — so the agent profile, the Search view, and
// the API all resolve identical NZ-calendar windows.
// Exported for tests — the Training Tips modal forwards these exact
// bounds to the API, so the vitest suite asserts against this function's
// output rather than re-deriving NZ-midnight math.
export function timeRangeToBounds(
  range: TimeRange,
  mode: RangeMode,
  customRange?: CustomDateRange | null,
): { from: string | null; to: string } {
  const { startMs, endMs } = resolvePeriodBoundsMs(range, mode, customRange);
  return {
    from: startMs != null ? new Date(startMs).toISOString() : null,
    // ``all_time`` has no upper bound server-side; these endpoints want a
    // concrete ``to``, so cap at "now" (same behaviour as before).
    to: endMs != null ? new Date(endMs).toISOString() : new Date().toISOString(),
  };
}

// Task #264 — extract the agent's first name for the diagnostic
// empty-state headlines ("Jane had no calls in this period…"). Falls
// back to "Agent" when the full name is missing so the headline never
// reads "null had…". Trims surrounding whitespace and splits on the
// first whitespace run so multi-word last names ("Jane Smith Doe")
// keep just "Jane".
export function agentFirstName(fullName: string | null | undefined): string {
  if (!fullName) return "Agent";
  const trimmed = fullName.trim();
  if (!trimmed) return "Agent";
  const head = trimmed.split(/\s+/, 1)[0];
  return head || "Agent";
}

export type SummaryRow = {
  label: string;
  pct: number;
  deltaPct: number | null;
  // Task #268 — extra context needed by the per-row tooltip: the
  // number of calls the row's percentage was computed from, and a
  // flag for whether a team-average reference exists for this row
  // (the ± pts paragraph is omitted when it doesn't).
  calls: number;
  hasTeamReference: boolean;
};

export type MeasureSummary = {
  overallPct: number;
  totalEvaluations: number;
  strengths: SummaryRow[];
  needsAttention: SummaryRow[];
  hasTeamComparison: boolean;
};

export function summariseMeasures(
  rows: { measure_id: string; label_name: string; detection_rate: number; calls: number }[],
  teamRows?: { measure_id: string; label_name: string; detection_rate: number; calls: number }[],
): MeasureSummary | null {
  if (!rows || rows.length === 0) return null;
  const totalEvaluations = rows.reduce((s, r) => s + r.calls, 0);
  // Volume-weighted overall: each (measure,label) pair contributes
  // proportional to how many calls it was evaluated against, so a
  // rarely-evaluated measure can't dominate the headline number.
  const weighted = totalEvaluations
    ? rows.reduce((s, r) => s + r.detection_rate * r.calls, 0) / totalEvaluations
    : rows.reduce((s, r) => s + r.detection_rate, 0) / rows.length;

  const teamMap = new Map<string, number>();
  for (const t of teamRows ?? []) {
    teamMap.set(`${t.measure_id}::${t.label_name}`, t.detection_rate);
  }

  type Decorated = {
    label: string;
    pct: number;
    deltaPct: number | null;
    calls: number;
    detection_rate: number;
    hasDelta: boolean;
    delta: number;
  };
  const decorated: Decorated[] = rows.map((r) => {
    const teamRate = teamMap.get(`${r.measure_id}::${r.label_name}`);
    const hasDelta = teamRate !== undefined;
    const delta = hasDelta ? r.detection_rate - (teamRate as number) : 0;
    return {
      label: r.label_name,
      pct: Math.round(r.detection_rate * 100),
      deltaPct: hasDelta ? Math.round(delta * 100) : null,
      calls: r.calls,
      detection_rate: r.detection_rate,
      hasDelta,
      delta,
    };
  });

  const hasTeamComparison = decorated.some((d) => d.hasDelta);

  let strengths: MeasureSummary["strengths"];
  let needsAttention: MeasureSummary["needsAttention"];
  if (hasTeamComparison) {
    // Sort by delta vs team avg: highest (most above) first for
    // strengths, lowest (most below) first for needs attention.
    // Pairs without a team-avg reference sink to the bottom of
    // either list so the comparison-backed picks lead.
    const sortDesc = [...decorated].sort((a, b) => {
      if (a.hasDelta !== b.hasDelta) return a.hasDelta ? -1 : 1;
      return (
        b.delta - a.delta ||
        b.calls - a.calls ||
        a.label.localeCompare(b.label)
      );
    });
    const sortAsc = [...decorated].sort((a, b) => {
      if (a.hasDelta !== b.hasDelta) return a.hasDelta ? -1 : 1;
      return (
        a.delta - b.delta ||
        b.calls - a.calls ||
        a.label.localeCompare(b.label)
      );
    });
    strengths = sortDesc
      .slice(0, 3)
      .map(({ label, pct, deltaPct, calls, hasDelta }) => ({
        label,
        pct,
        deltaPct,
        calls,
        hasTeamReference: hasDelta,
      }));
    needsAttention = sortAsc
      .slice(0, 3)
      .map(({ label, pct, deltaPct, calls, hasDelta }) => ({
        label,
        pct,
        deltaPct,
        calls,
        hasTeamReference: hasDelta,
      }));
  } else {
    const sorted = [...decorated].sort(
      (a, b) =>
        b.detection_rate - a.detection_rate ||
        b.calls - a.calls ||
        a.label.localeCompare(b.label),
    );
    strengths = sorted
      .slice(0, 3)
      .map(({ label, pct, deltaPct, calls, hasDelta }) => ({
        label,
        pct,
        deltaPct,
        calls,
        hasTeamReference: hasDelta,
      }));
    needsAttention = [...sorted]
      .reverse()
      .slice(0, 3)
      .map(({ label, pct, deltaPct, calls, hasDelta }) => ({
        label,
        pct,
        deltaPct,
        calls,
        hasTeamReference: hasDelta,
      }));
  }

  return {
    overallPct: Math.round(weighted * 100),
    totalEvaluations,
    strengths,
    needsAttention,
    hasTeamComparison,
  };
}
